import React, { useEffect, useMemo, useState } from 'react';
import { LuKeyRound, LuRefreshCw, LuShieldCheck, LuUserPlus, LuUsers } from 'react-icons/lu';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { useScope } from '../contexts/ScopeContext';
import { api } from '../utils/api';


const roleLabels = {
  global_admin: 'Global admin',
  project_admin: 'Project admin',
  manager: 'Manager',
  user: 'User',
};

const messageFor = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || fallback;
};

const PeopleAccess = ({ user, onLogout }) => {
  const scope = useScope();
  const organizationId = scope.organization?.id;
  const projectId = scope.project?.id;
  const [memberships, setMemberships] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizationForbidden, setOrganizationForbidden] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [invite, setInvite] = useState({ email: '', globalAdmin: false, projectRole: 'user' });
  const [selectedMember, setSelectedMember] = useState(null);
  const [permissionDetails, setPermissionDetails] = useState(null);
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [grants, setGrants] = useState([]);
  const [grantState, setGrantState] = useState('idle');
  const [grantError, setGrantError] = useState(null);
  const [pendingRoles, setPendingRoles] = useState({});
  const [grantForm, setGrantForm] = useState({ principalId: '', permission: 'terminal.connect', hours: 8, reason: '' });

  const bindingByUser = useMemo(
    () => Object.fromEntries(bindings.map((binding) => [binding.user_id, binding])),
    [bindings]
  );
  const memberById = useMemo(
    () => Object.fromEntries(memberships.map((membership) => [membership.user_id, membership])),
    [memberships]
  );

  const load = async () => {
    if (!organizationId || !projectId) return;
    setLoading(true);
    setError(null);
    setOrganizationForbidden(false);
    setMemberships([]);
    setInvitations([]);
    setBindings([]);
    setResources([]);
    setSelectedMember(null);
    setPermissionDetails(null);
    setGrants([]);
    setPendingRoles({});
    try {
      const [memberResult, invitationResult, bindingResult, resourceResult] = await Promise.allSettled([
        api.getMemberships(organizationId),
        api.getInvitations(organizationId),
        api.getProjectRoleBindings(organizationId, projectId),
        api.getResources(),
      ]);
      if (bindingResult.status === 'rejected') throw bindingResult.reason;
      if (resourceResult.status === 'rejected') throw resourceResult.reason;
      setBindings(bindingResult.value.data.role_bindings || []);
      const nextResources = resourceResult.value.data || [];
      setResources(nextResources);
      setSelectedResourceId((current) => (
        nextResources.some((resource) => resource.id === current) ? current : nextResources[0]?.id || ''
      ));
      if (memberResult.status === 'fulfilled') {
        setMemberships(memberResult.value.data.memberships || []);
        if (invitationResult.status === 'fulfilled') setInvitations(invitationResult.value.data.invitations || []);
      } else if (memberResult.reason?.response?.status === 403) {
        setOrganizationForbidden(true);
        setMemberships((bindingResult.value.data.role_bindings || []).map((binding) => ({
          user_id: binding.user_id,
          status: 'active',
          organization_role: null,
          revision: 0,
          user: { id: binding.user_id },
        })));
      } else {
        throw memberResult.reason;
      }
    } catch (requestError) {
      if (requestError?.code !== 'ERR_CANCELED') {
        setError(messageFor(requestError, 'Access administration could not be loaded.'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [scope.version, organizationId, projectId]);

  useEffect(() => {
    const loadGrants = async () => {
      if (!selectedResourceId || !organizationId || !projectId) {
        setGrants([]);
        setGrantState('idle'); setGrantError(null);
        return;
      }
      setGrantState('loading'); setGrantError(null); setGrants([]);
      try {
        const response = await api.getResourceGrants(organizationId, projectId, selectedResourceId);
        setGrants(response.data.grants || []);
        setGrantState('ready');
      } catch (requestError) {
        if (requestError?.code !== 'ERR_CANCELED') { setGrants([]); setGrantState('error'); setGrantError(messageFor(requestError, 'Server grants could not be loaded.')); }
      }
    };
    loadGrants();
  }, [selectedResourceId, organizationId, projectId, scope.version]);

  const submitInvite = async (event) => {
    event.preventDefault();
    if (invite.globalAdmin && !window.confirm('Grant Global Admin access across this organization? This is a high-privilege role.')) return;
    setBusyKey('invite');
    try {
      await api.createInvitation(organizationId, {
        email: invite.email,
        organization_role: invite.globalAdmin ? 'global_admin' : null,
        project_roles: invite.globalAdmin ? [] : [{ project_id: projectId, role: invite.projectRole }],
      });
      setInvite({ email: '', globalAdmin: false, projectRole: 'user' });
      toast.success('Invitation queued for email delivery');
      await load();
    } catch (requestError) {
      toast.error(messageFor(requestError, 'Member could not be added'));
    } finally {
      setBusyKey(null);
    }
  };

  const resendInvitation = async (invitation) => {
    setBusyKey(`invitation:${invitation.id}`);
    try { await api.resendInvitation(organizationId, invitation.id); toast.success('Invitation email queued'); await load(); }
    catch (requestError) { toast.error(messageFor(requestError, 'Invitation could not be resent')); }
    finally { setBusyKey(null); }
  };

  const revokeInvitation = async (invitation) => {
    if (!window.confirm(`Revoke the invitation for ${invitation.email}?`)) return;
    setBusyKey(`invitation:${invitation.id}`);
    try { await api.revokeInvitation(organizationId, invitation.id); toast.success('Invitation revoked'); await load(); }
    catch (requestError) { toast.error(messageFor(requestError, 'Invitation could not be revoked')); }
    finally { setBusyKey(null); }
  };

  const updateMembership = async (membership, changes) => {
    const elevates = changes.organization_role === 'global_admin' && membership.organization_role !== 'global_admin';
    if (elevates && !window.confirm('Promote this person to Global Admin for the whole organization?')) return;
    setBusyKey(`membership:${membership.user_id}`);
    try {
      await api.updateMembership(organizationId, membership.user_id, {
        status: changes.status ?? membership.status,
        organization_role: Object.prototype.hasOwnProperty.call(changes, 'organization_role')
          ? changes.organization_role
          : membership.organization_role,
        expected_revision: membership.revision,
      });
      toast.success('Organization access updated');
      await load();
    } catch (requestError) {
      toast.error(messageFor(requestError, 'Organization access could not be changed'));
    } finally {
      setBusyKey(null);
    }
  };

  const setProjectRole = async (membership, role) => {
    const binding = bindingByUser[membership.user_id];
    if (role === 'project_admin' && binding?.role !== 'project_admin' && !window.confirm('Grant Project Admin access? This role can manage providers, servers and project access.')) return false;
    setBusyKey(`role:${membership.user_id}`);
    try {
      await api.setProjectRoleBinding(organizationId, projectId, membership.user_id, {
        role,
        status: 'active',
        expected_revision: binding?.revision || 0,
      });
      toast.success('Project role updated');
      await load();
      return true;
    } catch (requestError) {
      toast.error(messageFor(requestError, 'Project role could not be changed'));
      return false;
    } finally {
      setBusyKey(null);
    }
  };

  const saveStagedRole = async (membership) => {
    const nextRole = pendingRoles[membership.user_id];
    if (!nextRole || nextRole === bindingByUser[membership.user_id]?.role) return;
    const currentRole = bindingByUser[membership.user_id]?.role || 'none';
    if (!window.confirm(`Change this person from ${roleLabels[currentRole] || currentRole} to ${roleLabels[nextRole] || nextRole} in ${scope.project?.name}?`)) return;
    const saved = await setProjectRole(membership, nextRole);
    if (saved) setPendingRoles((current) => { const next = { ...current }; delete next[membership.user_id]; return next; });
  };

  const explainAccess = async (membership) => {
    setSelectedMember(membership.user_id);
    setPermissionDetails(null);
    try {
      const response = await api.getEffectivePermissions(organizationId, projectId, membership.user_id);
      setPermissionDetails(response.data);
    } catch (requestError) {
      toast.error(messageFor(requestError, 'Effective access could not be explained'));
      setSelectedMember(null);
    }
  };

  const createGrant = async (event) => {
    event.preventDefault();
    if (grantState !== 'ready') { toast.error('Load the authoritative grant list before creating a grant.'); return; }
    if (['resource.power', 'resource.delete', 'terminal.connect'].includes(grantForm.permission)) {
      const label = grantForm.permission === 'terminal.connect' ? 'terminal access' : grantForm.permission.replace('.', ' ');
      if (!window.confirm(`Grant ${label} to this person for the selected server until it expires?`)) return;
    }
    setBusyKey('grant');
    try {
      const idempotencyKey = window.crypto?.randomUUID?.() || `grant-${Date.now()}`;
      await api.createResourceGrant(organizationId, projectId, selectedResourceId, {
        principal_kind: 'human',
        principal_id: grantForm.principalId,
        permissions: [grantForm.permission],
        duration_hours: Number(grantForm.hours),
        provenance: 'direct',
        source_request_id: null,
        reason: grantForm.reason,
      }, idempotencyKey);
      toast.success('Time-bound resource grant created');
      setGrantForm((current) => ({ ...current, reason: '' }));
      const response = await api.getResourceGrants(organizationId, projectId, selectedResourceId);
      setGrants(response.data.grants || []);
    } catch (requestError) {
      toast.error(messageFor(requestError, 'Resource grant could not be created'));
    } finally {
      setBusyKey(null);
    }
  };

  const revokeGrant = async (grant) => {
    if (!window.confirm('Revoke this resource grant immediately? Active terminal sessions will be denied when they reauthorize.')) return;
    setBusyKey(`grant:${grant.id}`);
    try {
      await api.revokeResourceGrant(organizationId, projectId, grant.id, {
        expected_revision: grant.revision,
        reason: 'Revoked from access administration',
      });
      toast.success('Grant revoked');
      const response = await api.getResourceGrants(organizationId, projectId, selectedResourceId);
      setGrants(response.data.grants || []);
    } catch (requestError) {
      toast.error(messageFor(requestError, 'Grant could not be revoked'));
    } finally {
      setBusyKey(null);
    }
  };

  const fieldClass = 'rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-200 outline-none focus:border-amber-500/60';

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Navbar user={user} onLogout={onLogout} />
      <main className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-amber-400">Organization access</p>
            <h1 className="mt-2 text-3xl font-semibold">People & access</h1>
            <p className="mt-2 max-w-2xl text-sm text-zinc-400">Organization membership, project roles, and expiring server grants are separate on purpose. This page shows the effective result.</p>
          </div>
          <button onClick={load} className="flex items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800"><LuRefreshCw /> Refresh</button>
        </div>

        {loading && <div className="mt-10 rounded-xl border border-zinc-800 bg-zinc-900/60 p-8 text-center text-zinc-400" role="status">Loading people and access…</div>}
        {error && <div className="mt-8 rounded-xl border border-red-500/30 bg-red-500/10 p-5 text-red-200" role="alert">{error} <button onClick={load} className="ml-2 underline">Try again</button></div>}

        {!loading && !error && <div className="mt-8 space-y-8">
          {!organizationForbidden && <section className="glass-card rounded-xl border border-zinc-800/60 p-5 sm:p-6">
            <div className="flex items-center gap-3"><LuUserPlus className="text-amber-400" /><div><h2 className="font-semibold">Invite a person</h2><p className="text-sm text-zinc-500">They will receive a one-time activation link valid for 72 hours.</p></div></div>
            <form onSubmit={submitInvite} className="mt-5 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto_auto] md:items-center">
              <input className={fieldClass} type="email" placeholder="person@company.com" value={invite.email} onChange={(event) => setInvite({ ...invite, email: event.target.value })} required aria-label="Member email" />
              <select className={fieldClass} value={invite.projectRole} disabled={invite.globalAdmin} onChange={(event) => setInvite({ ...invite, projectRole: event.target.value })} aria-label="Initial project role"><option value="user">User</option><option value="manager">Manager</option><option value="project_admin">Project admin</option></select>
              <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={invite.globalAdmin} onChange={(event) => setInvite({ ...invite, globalAdmin: event.target.checked })} /> Global Admin</label>
              <button disabled={busyKey === 'invite'} className="rounded-lg bg-amber-500 px-4 py-2 font-semibold text-zinc-950 disabled:opacity-50">{busyKey === 'invite' ? 'Sending…' : 'Send invitation'}</button>
            </form>
            {!!invitations.length && <div className="mt-5 divide-y divide-zinc-800 rounded-lg border border-zinc-800">{invitations.map((item) => <div key={item.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"><div><p className="text-sm text-zinc-200">{item.email}</p><p className="mt-1 text-xs text-zinc-500">{item.status} · expires {new Date(item.expires_at).toLocaleString()}</p></div>{item.status === 'pending' && <div className="flex gap-2"><button disabled={busyKey === `invitation:${item.id}`} onClick={() => resendInvitation(item)} className="rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300">Resend</button><button disabled={busyKey === `invitation:${item.id}`} onClick={() => revokeInvitation(item)} className="rounded-lg border border-red-500/30 px-3 py-2 text-sm text-red-300">Revoke</button></div>}</div>)}</div>}
          </section>}

          <section className="glass-card overflow-hidden rounded-xl border border-zinc-800/60">
            <div className="border-b border-zinc-800 p-5 sm:p-6"><div className="flex items-center gap-3"><LuUsers className="text-amber-400" /><div><h2 className="font-semibold">People in {scope.project?.name}</h2><p className="text-sm text-zinc-500">Project roles apply only to the selected project.</p></div></div></div>
            {organizationForbidden && <div className="border-b border-blue-500/20 bg-blue-500/10 p-4 text-sm text-blue-200">You can manage this project’s existing role bindings, but only a Global Admin can change organization membership.</div>}
            <div className="divide-y divide-zinc-800">
              {memberships.map((membership) => {
                const binding = bindingByUser[membership.user_id];
                const identity = membership.user || {};
                return <div key={membership.user_id} className="grid gap-4 p-5 lg:grid-cols-[minmax(0,1.4fr)_auto_auto_auto] lg:items-center">
                  <div className="min-w-0"><p className="truncate font-medium">{identity.name || identity.email || membership.user_id}</p><p className="truncate text-sm text-zinc-500">{identity.email || membership.user_id}</p><div className="mt-2 flex flex-wrap gap-2 text-xs"><span className={`rounded-full px-2 py-1 ${membership.status === 'active' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-zinc-800 text-zinc-400'}`}>{membership.status}</span>{membership.organization_role && <span className="rounded-full bg-purple-500/10 px-2 py-1 text-purple-300">{roleLabels[membership.organization_role]}</span>}</div></div>
                  <div><label className="flex flex-col gap-1 text-xs text-zinc-500">Project role<select className={fieldClass} value={pendingRoles[membership.user_id] ?? binding?.role ?? ''} disabled={membership.status !== 'active' || busyKey === `role:${membership.user_id}`} onChange={(event) => setPendingRoles((current) => ({ ...current, [membership.user_id]: event.target.value }))}><option value="" disabled>Not assigned</option><option value="user">User</option><option value="manager">Manager</option><option value="project_admin">Project admin</option></select></label>{pendingRoles[membership.user_id] && pendingRoles[membership.user_id] !== binding?.role && <div className="mt-2 flex gap-2"><button onClick={() => saveStagedRole(membership)} className="min-h-10 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-zinc-950">Save role</button><button onClick={() => setPendingRoles((current) => { const next = { ...current }; delete next[membership.user_id]; return next; })} className="min-h-10 rounded-lg px-3 text-xs text-zinc-400">Cancel</button></div>}</div>
                  {!organizationForbidden && <div className="flex flex-wrap gap-2"><button onClick={() => updateMembership(membership, { status: membership.status === 'active' ? 'suspended' : 'active' })} disabled={busyKey === `membership:${membership.user_id}`} className="rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800">{membership.status === 'active' ? 'Suspend' : 'Activate'}</button><button onClick={() => updateMembership(membership, { organization_role: membership.organization_role === 'global_admin' ? null : 'global_admin' })} className="rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800">{membership.organization_role === 'global_admin' ? 'Remove global role' : 'Make global admin'}</button></div>}
                  <button onClick={() => explainAccess(membership)} className="rounded-lg bg-zinc-800 px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700">Explain access</button>
                </div>;
              })}
              {!memberships.length && <div className="p-8 text-center text-zinc-500">No project members are available.</div>}
            </div>
          </section>

          {selectedMember && <section className="glass-card rounded-xl border border-zinc-800/60 p-5 sm:p-6">
            <div className="flex items-center justify-between gap-4"><div className="flex items-center gap-3"><LuShieldCheck className="text-emerald-400" /><div><h2 className="font-semibold">Effective access</h2><p className="text-sm text-zinc-500">{memberById[selectedMember]?.user?.email || selectedMember} in {scope.project?.name}</p></div></div><button onClick={() => setSelectedMember(null)} className="text-sm text-zinc-400">Close</button></div>
            {!permissionDetails ? <p className="mt-5 text-sm text-zinc-500" role="status">Calculating effective permissions…</p> : <div className="mt-5"><div className="flex flex-wrap gap-2">{permissionDetails.permissions?.filter((item) => item.allowed).map((item) => <span key={item.permission} className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs text-emerald-300">{item.permission}</span>)}</div>{!permissionDetails.permissions?.some((item) => item.allowed) && <p className="text-sm text-zinc-500">No active permissions in this project.</p>}<div className="mt-5 space-y-2">{permissionDetails.resource_grants?.map((grant) => <div key={grant.id} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 text-sm"><span className={grant.active ? 'text-emerald-300' : 'text-zinc-500'}>{grant.active ? 'Active' : 'Inactive'}</span> · {grant.permissions.join(', ')} · server {grant.resource_id} · expires {new Date(grant.expires_at).toLocaleString()}</div>)}</div></div>}
          </section>}

          <section className="glass-card rounded-xl border border-zinc-800/60 p-5 sm:p-6">
            <div className="flex items-center gap-3"><LuKeyRound className="text-amber-400" /><div><h2 className="font-semibold">Server grants</h2><p className="text-sm text-zinc-500">Time-bound, person-specific access. Expired and revoked grants do not authorize actions.</p></div></div>
            <select className={`${fieldClass} mt-5 w-full`} value={selectedResourceId} onChange={(event) => setSelectedResourceId(event.target.value)} aria-label="Server"><option value="">Select a server</option>{resources.map((resource) => <option key={resource.id} value={resource.id}>{resource.name}</option>)}</select>
            {selectedResourceId && <>{grantState === 'loading' && <p className="mt-4 text-sm text-zinc-500" role="status">Loading authoritative grants…</p>}{grantState === 'error' && <div className="mt-4 rounded-lg border border-red-500/25 bg-red-500/10 p-4 text-sm text-red-200" role="alert">{grantError}<button onClick={() => { setSelectedResourceId(''); window.setTimeout(() => setSelectedResourceId(resources[0]?.id || ''), 0); }} className="ml-2 underline">Retry</button></div>}<form onSubmit={createGrant} aria-disabled={grantState !== 'ready'} className="mt-4 grid gap-3 lg:grid-cols-5"><label className="text-xs text-zinc-500">Person<select required disabled={grantState !== 'ready'} className={`${fieldClass} mt-1 w-full`} value={grantForm.principalId} onChange={(event) => setGrantForm({ ...grantForm, principalId: event.target.value })}><option value="">Choose person</option>{memberships.filter((item) => item.status === 'active').map((item) => <option key={item.user_id} value={item.user_id}>{item.user?.name || item.user?.email || item.user_id}</option>)}</select></label><label className="text-xs text-zinc-500">Permission<select disabled={grantState !== 'ready'} className={`${fieldClass} mt-1 w-full`} value={grantForm.permission} onChange={(event) => setGrantForm({ ...grantForm, permission: event.target.value })}><option value="terminal.connect">Terminal access</option><option value="resource.read">View server</option><option value="resource.metadata.manage">Edit metadata</option><option value="resource.power">Power control</option><option value="resource.delete">Delete server</option></select></label><label className="text-xs text-zinc-500">Duration in hours<input disabled={grantState !== 'ready'} className={`${fieldClass} mt-1 w-full`} type="number" min="1" max="168" value={grantForm.hours} onChange={(event) => setGrantForm({ ...grantForm, hours: event.target.value })} /></label><label className="text-xs text-zinc-500">Reason<input disabled={grantState !== 'ready'} required minLength="3" maxLength="500" className={`${fieldClass} mt-1 w-full`} value={grantForm.reason} onChange={(event) => setGrantForm({ ...grantForm, reason: event.target.value })} /></label><button disabled={grantState !== 'ready' || busyKey === 'grant'} className="rounded-lg bg-amber-500 px-4 py-2 font-semibold text-zinc-950 disabled:opacity-50">Create grant</button></form>{grantState === 'ready' && <div className="mt-5 divide-y divide-zinc-800">{grants.map((grant) => { const expires = new Date(grant.expires_at); const active = grant.status === 'active' && expires > new Date(); return <div key={grant.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"><div><p className="text-sm text-zinc-200">{grant.permissions.join(', ')} → {memberById[grant.principal_id]?.user?.email || grant.principal_id}</p><p className="mt-1 text-xs text-zinc-500">{active ? `Expires ${expires.toLocaleString()}` : `${grant.status} · expired ${expires.toLocaleString()}`} · {grant.provenance}</p></div>{active && <button onClick={() => revokeGrant(grant)} disabled={busyKey === `grant:${grant.id}`} className="rounded-lg border border-red-500/30 px-3 py-2 text-sm text-red-300 hover:bg-red-500/10">Revoke</button>}</div>; })}{!grants.length && <p className="py-6 text-center text-sm text-zinc-500">No grants for this server.</p>}</div>}</>}
          </section>
        </div>}
      </main>
    </div>
  );
};

export default PeopleAccess;
